import { getDefaultStore, useAtomValue, useSetAtom, useStore } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'
import {
  normalizeViewContext,
  reopenView,
  viewIdentity,
  type ViewContexts,
  type ViewId,
  type ViewRequiresContext,
} from './view-definitions'
import { isViewPopoutPointer, openViewWindow } from './open-view-window'
import { canPopOutView, isViewPopout } from './surface'
import { sidepanelStateAtom } from '@renderer/overlay/atoms'
import { useSidepanelSurface } from './profile/use-sidepanel-profile'
import {
  backToRoot,
  canGoBack,
  canGoForward,
  dropForwardEntries,
  goBack,
  goForward,
  jumpTo,
  openViewInHistory,
  replaceViewById,
  resolveGuardedNavigation,
  topEntry,
  updateTopView,
  type ViewHistory,
} from './view-history'
import {
  HOME_VIEW_ID,
  createSidepanelViewHistory,
  viewDraftsAtom,
  useViewScope,
  viewHistoryAtom,
  viewOpenAtom,
  viewScrollPositionsAtom,
  viewSectionDisclosureAtom,
  viewTabAtom,
  type SidepanelView,
} from './view-state-atoms'

/**
 * View router history (ADR 0001) — the single source of navigation truth for
 * the sidepanel. Home is the root entry; drill-in Views are entries above it.
 *
 * Held in GlobalState (HIG-6022) so a hard refresh restores the stack — the
 * open view and its back/forward trail — instead of dropping the user on a
 * fresh Home. Every *consumer* — the router, Mini, the Toaster hooks, the
 * Assistant — is still a component of the one overlay renderer, and the main
 * process still reads only `sidepanel.view.open` (a boolean); the store is the
 * persistence surface, not a cross-renderer contract. The cost is the one
 * `ipc-performance.mdc` opens with — each write structured-clones every entry's
 * context (a whole StagedAction, a whole CalendarEvent) to every renderer, even
 * cursor-only back/forward — accepted here because writes happen at human
 * navigation speed. Nothing higher-frequency may ever land in this atom.
 *
 * A popout window runs these same hooks against a renderer-local copy of the
 * atom, rooted on the popped View instead of Home (see `view-state-atoms`).
 *
 * The atoms themselves live in `view-state-atoms.ts` so that this module can be
 * hot-reloaded without recreating them (see the note there).
 */
export { HOME_VIEW_ID, createSidepanelViewHistory, viewDraftsAtom, viewHistoryAtom, type SidepanelView }

export const useViewHistory = () => useAtomValue(useViewScope().history)

/**
 * Imperative read of the mounted View for non-atom consumers (the Toaster
 * hooks, which run outside React's render cycle).
 */
export const getSidepanelTopView = (): SidepanelView => topEntry(getDefaultStore().get(viewHistoryAtom)).view

export const useSetViewOpen = () => {
  return useSetAtom(viewOpenAtom)
}

/**
 * View navigation actions over the view-router history (ADR 0001).
 */
export interface OpenViewOptions {
  sourceLabel?: string // Label of the section/view that opened this one, for the back tooltip
  skipTransition?: boolean // Skip the entrance animation (for Framer Motion layoutId handoffs)
  /**
   * The click that asked for the open, when there was one. ⌘/Ctrl-click opens
   * the View in its own window instead of pushing it here; `popout-windows-by-default`
   * inverts that (plain click pops out, modifier stays in-panel). Rows pass the
   * event through and never branch on the modifier themselves.
   */
  pointerEvent?: Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'preventDefault' | 'stopPropagation'>
  /**
   * Open in a window regardless of the pointer — for controls whose whole job
   * is the popout (the hover cluster's "Open in new window"), rather than a row
   * click that only pops out under a modifier.
   */
  popout?: boolean
}

/**
 * How one open was asked for: the click behind it, or a forced popout. Rows and
 * the hooks they delegate to take this and spread it into `openView`'s options,
 * so the popout decision is made in one place.
 */
export type ViewOpenTarget = Pick<OpenViewOptions, 'pointerEvent' | 'popout'>

/**
 * The two controls a row hangs off one target-taking open: its click
 * (`onOpen`) and its "Open in new window" action (`onPopout`).
 */
export const useViewOpenHandlers = (open: (target: ViewOpenTarget) => void) =>
  useMemo(
    () => ({
      onOpen: (pointerEvent?: ViewOpenTarget['pointerEvent']) => open({ pointerEvent }),
      onPopout: () => open({ popout: true }),
    }),
    [open],
  )

/**
 * Opening a view: the id fixes which context is required, optional, or absent.
 * Exported so every wrapper around `openView` reuses this contract instead of
 * re-declaring the tuple and then casting its own copy away.
 */
export type OpenViewFunction = <K extends ViewId>(
  ...args: ViewRequiresContext<K> extends true
    ? [id: K, context: ViewContexts[K], options?: OpenViewOptions]
    : ViewContexts[K] extends void | undefined
      ? [id: K, context?: undefined, options?: OpenViewOptions]
      : [id: K, context?: ViewContexts[K], options?: OpenViewOptions]
) => void

export interface NavigateOptions {
  /**
   * Not asked for by the user (a timer, a background event): skipped outright
   * while the mounted view holds unsent edits, rather than confirmed.
   */
  automatic?: boolean
}

/**
 * The history setter every navigation goes through, consulting the mounted
 * view's leave guard (`resolveGuardedNavigation`). A confirmed move re-runs the
 * transform on approval, against the live history. Returns whether the
 * navigation was applied, so callers with a physical gesture in flight (the
 * swipe) know to snap back. A popout window is always showing.
 */
const useGuardedSetViewHistory = () => {
  const store = useStore()
  const scope = useViewScope()
  const setViewHistory = useSetAtom(scope.history)
  return useCallback(
    (
      transform: (h: ViewHistory<SidepanelView>) => ViewHistory<SidepanelView>,
      { automatic = false }: NavigateOptions = {},
    ): boolean => {
      const history = store.get(scope.history)
      const guard = store.get(scope.navigationGuard)
      const surfaceShowing = isViewPopout || store.get(sidepanelStateAtom) === 'open'
      const navigation = resolveGuardedNavigation(history, transform(history), guard, { automatic, surfaceShowing })
      switch (navigation.type) {
        case 'skip':
          return false
        case 'confirm':
          navigation.confirm(() => setViewHistory(transform))
          return false
        case 'apply':
          setViewHistory(transform)
          return true
      }
    },
    [store, scope, setViewHistory],
  )
}

export const useViewActions = () => {
  const setViewHistory = useGuardedSetViewHistory()

  const openView = useCallback(
    (<K extends ViewId>(id: K, context?: ViewContexts[K], options?: OpenViewOptions) => {
      const view: SidepanelView = {
        id,
        // Enriched with what the session already resolved about the document
        // (a note's calendar event, say), so the identity dedupe below can
        // recognize an open under one of its other names.
        context: normalizeViewContext(id, context) as ViewContexts[K],
        sourceLabel: options?.sourceLabel,
        skipEntrance: options?.skipTransition ?? false,
      }

      // A popout window is already the window. Every open there pushes its own
      // stack, whatever the pointer or the popout-by-default flag says.
      const pointerEvent = options?.pointerEvent
      const popout = options?.popout || (pointerEvent && isViewPopoutPointer(pointerEvent))
      if (canPopOutView && popout) {
        pointerEvent?.preventDefault()
        pointerEvent?.stopPropagation()
        openViewWindow({ id, context: view.context })
        return
      }

      // Browser-like push, collapsing only onto the same document already open
      // below the cursor. The policy itself lives in `view-history` so it's
      // unit-tested; the registry supplies what "same document" means.
      setViewHistory((history) => openViewInHistory(history, view, viewIdentity, reopenView))
    }) as OpenViewFunction,
    [setViewHistory],
  )

  const closeView = useCallback((options?: NavigateOptions) => setViewHistory(goBack, options), [setViewHistory])

  const closeAllViews = useCallback(
    (options?: NavigateOptions) => {
      setViewHistory(backToRoot, options)
    },
    [setViewHistory],
  )

  const forwardView = useCallback(() => setViewHistory(goForward), [setViewHistory])

  return useMemo(
    () => ({ openView, closeView, closeAllViews, forwardView }),
    [openView, closeView, closeAllViews, forwardView],
  )
}

/**
 * Refresh the *mounted* view's context in place, keeping its entry key (so
 * per-entry scroll and drafts survive) and forward history. For data the open
 * view learns about itself after opening — a chat adopting its real conversation
 * id, a meeting resolving which note and calendar event it's showing.
 *
 * Only the top entry is mounted, so this is the only form a view needs: patching
 * every entry sharing the id would stamp one document's payload onto another's
 * entry whenever two of the same kind are stacked.
 */
export const useUpdateTopViewContext = <K extends ViewId>(id: K) => {
  const setViewHistory = useSetAtom(useViewScope().history)
  return useCallback(
    (update: (context: ViewContexts[K] | undefined) => ViewContexts[K] | undefined) => {
      setViewHistory((history) =>
        updateTopView(history, id, (view) => ({
          ...view,
          context: update(view.context as ViewContexts[K] | undefined),
        })),
      )
    },
    [setViewHistory, id],
  )
}

/**
 * Whether a history entry is the one currently mounted — for teardown that has
 * to tell "the entry was left" from "this tree unmounted with the entry still
 * open" (a StrictMode remount, the panel closing). Read imperatively in a
 * cleanup, so it hands back a getter rather than a value.
 */
export const useIsEntryOnTop = () => {
  const store = useStore()
  const { history } = useViewScope()
  return useCallback((entryKey: number) => topEntry(store.get(history)).key === entryKey, [store, history])
}

/**
 * Swap an open view for another, keeping the views below it reachable via
 * back. Used when a view discovers it can't render what it was opened for and
 * hands off to a different one.
 */
export const useReplaceView = () => {
  const setViewHistory = useSetAtom(useViewScope().history)
  return useCallback(
    <K extends ViewId>(id: ViewId, replacement: { id: K; context?: ViewContexts[K] }) => {
      setViewHistory((history) => replaceViewById(history, id, { ...replacement }))
    },
    [setViewHistory],
  )
}

/**
 * View-router navigation — history traversal state + actions for the top-bar
 * chevrons, hotkeys, and swipe handlers.
 */
export const useViewNavigation = () => {
  const history = useViewHistory()
  const setViewHistory = useGuardedSetViewHistory()

  const goBackView = useCallback(() => {
    setViewHistory(goBack)
  }, [setViewHistory])
  const goForwardView = useCallback(() => {
    setViewHistory(goForward)
  }, [setViewHistory])
  const jumpToIndex = useCallback(
    (index: number) => {
      setViewHistory((h) => jumpTo(h, index))
    },
    [setViewHistory],
  )

  const below = history.entries[history.index - 1]?.view

  return useMemo(
    () => ({
      canGoBack: canGoBack(history),
      canGoForward: canGoForward(history),
      topView: topEntry(history).view,
      topEntryKey: topEntry(history).key,
      /** The view back would reveal, or undefined when that's Home. */
      parentView: below?.id === HOME_VIEW_ID ? undefined : below,
      /** The view forward would reveal. */
      nextForwardView: history.entries[history.index + 1]?.view,
      /**
       * The back trail for the breadcrumb menu, nearest-first. Carries each
       * entry's absolute index so a jump doesn't have to re-derive it — the
       * menu can be rendered from a stale snapshot and `jumpTo` still ignores
       * an index that no longer exists.
       */
      backTrail: history.entries
        .slice(0, history.index)
        .map((entry, i) => ({ index: i, key: entry.key, view: entry.view }))
        .reverse(),
      /** The forward trail, nearest-first. */
      forwardTrail: history.entries
        .slice(history.index + 1)
        .map((entry, i) => ({ index: history.index + 1 + i, key: entry.key, view: entry.view })),
      goBack: goBackView,
      goForward: goForwardView,
      jumpToIndex,
    }),
    [history, below, goBackView, goForwardView, jumpToIndex],
  )
}

export const useGetViewScrollPosition = () => {
  const store = useStore()
  return useCallback((entryKey: number) => store.get(viewScrollPositionsAtom)[entryKey] ?? 0, [store])
}

export const useSetViewScrollPosition = () => {
  const setPositions = useSetAtom(viewScrollPositionsAtom)
  return useCallback(
    (entryKey: number, scrollTop: number) => {
      setPositions((positions) =>
        positions[entryKey] === scrollTop ? positions : { ...positions, [entryKey]: scrollTop },
      )
    },
    [setPositions],
  )
}

/** Collapsed/expanded overrides for one history entry's sections. */
export const useViewSectionDisclosure = (entryKey: number) => {
  const disclosures = useAtomValue(viewSectionDisclosureAtom)
  return disclosures[entryKey]
}

export const useSetViewSectionDisclosure = () => {
  const setDisclosures = useSetAtom(viewSectionDisclosureAtom)
  return useCallback(
    (entryKey: number, section: string, expanded: boolean) => {
      setDisclosures((current) =>
        current[entryKey]?.[section] === expanded
          ? current
          : { ...current, [entryKey]: { ...current[entryKey], [section]: expanded } },
      )
    },
    [setDisclosures],
  )
}

/** The tab one history entry picked by hand, if any. */
export const useViewTab = (entryKey: number) => {
  const tabs = useAtomValue(viewTabAtom)
  return tabs[entryKey]
}

/** `undefined` clears the pick, so the view's own default applies again. */
export const useSetViewTab = () => {
  const setTabs = useSetAtom(viewTabAtom)
  return useCallback(
    (entryKey: number, tab: string | undefined) => {
      setTabs((current) => {
        if (current[entryKey] === tab) return current
        if (tab === undefined) {
          const next = { ...current }
          delete next[entryKey]
          return next
        }
        return { ...current, [entryKey]: tab }
      })
    },
    [setTabs],
  )
}

/**
 * Drop per-entry state for entries no longer in history (truncated by a push).
 * Mount once at the sidepanel root.
 */
export const useViewEntryStatePruning = () => {
  const history = useViewHistory()
  const setScrollPositions = useSetAtom(viewScrollPositionsAtom)
  const setDrafts = useSetAtom(viewDraftsAtom)
  const setSectionDisclosures = useSetAtom(viewSectionDisclosureAtom)
  const setTabs = useSetAtom(viewTabAtom)

  useEffect(() => {
    const live = new Set(history.entries.map((entry) => entry.key))
    const prune = <T>(record: Record<number, T>): Record<number, T> => {
      const keys = Object.keys(record).map(Number)
      if (keys.every((key) => live.has(key))) return record
      return Object.fromEntries(keys.filter((key) => live.has(key)).map((key) => [key, record[key]]))
    }
    setScrollPositions(prune)
    setDrafts(prune)
    setSectionDisclosures(prune)
    setTabs(prune)
  }, [history, setScrollPositions, setDrafts, setSectionDisclosures, setTabs])
}

/**
 * Home navigation keeps no forward history, so this drops every entry ahead of
 * the cursor after a back, a jump down the trail, or a restore from the store.
 * Mount once at the surface root.
 */
export const useHomeNavigationHistory = () => {
  const isHomeNavigation = useSidepanelSurface('navigation') === 'home'
  const history = useViewHistory()
  const setViewHistory = useSetAtom(viewHistoryAtom)

  useEffect(() => {
    if (isHomeNavigation && canGoForward(history)) {
      setViewHistory(dropForwardEntries)
    }
  }, [isHomeNavigation, history, setViewHistory])
}

/**
 * Wipe navigation state back to a fresh Home — history *and* the per-entry
 * state keyed on it. For sign-out, where `closeAllViews` isn't enough: that
 * only moves the cursor to the root, so every View the signed-out session
 * opened stays reachable through the forward chevron, drafts and trail labels
 * included. Pruning can't cover it either — the entries are still live.
 */
export const useResetViews = () => {
  const setViewHistory = useSetAtom(viewHistoryAtom)
  const setScrollPositions = useSetAtom(viewScrollPositionsAtom)
  const setDrafts = useSetAtom(viewDraftsAtom)
  const setSectionDisclosures = useSetAtom(viewSectionDisclosureAtom)
  const setTabs = useSetAtom(viewTabAtom)

  return useCallback(() => {
    setViewHistory(createSidepanelViewHistory())
    // The fresh root reuses Home's key, so its state survives pruning — clear
    // it here or the next user inherits the previous one's Home draft.
    setScrollPositions({})
    setDrafts({})
    setSectionDisclosures({})
    setTabs({})
  }, [setViewHistory, setScrollPositions, setDrafts, setSectionDisclosures, setTabs])
}
